import { iopIcon, rxIcon, specIcon, visionIcon } from '../components/common/Icons';
import { LOCAL_ID_PREFIX } from '../constants';
import { OP_FGP_COPY_FROM } from '../store/actions/prescriptionPad';
import { OpSpec, OpIop, OpVision, PX_OP_KEYS, Prescription } from '../store/types/prescription';
import { generateElementId, uniqueBy } from '../store/utils';

let values:
    | { [k: string]: { id: string; value: string; unit: string; custom: string }[] }
    | undefined = undefined;

export const fetchOpValues = async (): Promise<any> => {
    fetch('/op/values.json')
        .then((response) => {
            return response.json();
        })
        .then((data) => {
            values = data;
        })
        .catch((error) => {
            console.error('Error in fetchOpValues:', error);
        });
};

export type OptionTypeBase = {
    id: string;
    eye: { value: string };
};

export const eyeNameMap: { [k in 'RE' | 'LE']: string } = {
    RE: 'Right Eye',
    LE: 'Left Eye',
};

export const getUITable = <T extends OptionTypeBase>(opVisions: T[], pxKey: PX_OP_KEYS): T[] => {
    const defaultValue: (OptionTypeBase & { id: string; name?: string })[] = getDefaulOPValue(
        pxKey,
    );

    let returnArray = opVisions || defaultValue;
    if (opVisions?.length === defaultValue.length) {
        returnArray = opVisions;
    } else if (opVisions?.length < defaultValue.length) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        returnArray = defaultValue.map((d) => {
            const found = opVisions?.find((o) => o.eye.value === d.eye.value);
            return found || d;
        });
    } else if (!opVisions?.length) {
        returnArray = defaultValue as T[];
    }
    return returnArray.sort((a, b) => {
        return b.eye.value.length - a.eye.value.length || b.eye.value.localeCompare(a.eye.value);
    });
};

const generativeOptionsFields = new Set(['nct', 'gat', 'cct', 'ciop', 'sph', 'cyl', 'axis', 'add']);

export const onOpPropertySearch = <T>(
    key: keyof T,
): ((value: string) => Promise<{ id: string; value: string; custom: string; unit: string }[]>) => {
    return (value: string) => {
        const text = value.trim();
        const possibileValues = generativeOptionsFields.has(key as string)
            ? generateOptions(key as string)
            : values?.[key as string];

        if (!possibileValues?.length) {
            if (text) {
                return Promise.resolve([...generateCustomItem(key as string, text)]);
            } else {
                return Promise.resolve([]);
            }
        }
        const matchingSuggestions = !text
            ? possibileValues || []
            : possibileValues
                  ?.filter((t) => t.custom.toLowerCase().includes(value.toLowerCase()))
                  .sort(
                      (a, b) =>
                          a.custom.indexOf(text) - b.custom.indexOf(text) ||
                          a.custom.length - b.custom.length,
                  ) || [];
        if (
            text &&
            !matchingSuggestions?.find((t) => t.custom.toLowerCase().includes(text.toLowerCase()))
        ) {
            matchingSuggestions.push(...generateCustomItem(key as string, text));
        }
        return Promise.resolve(uniqueBy('custom', matchingSuggestions));
    };
};

export const generateCustomItem = (
    key: string,
    text: string,
): { id: string; value: string; unit: string; custom: string }[] => {
    // replace all units from the text
    const value = text.replace(/mmHg|µm|\+|-|°/g, '');
    if (['nct', 'gat', 'cct', 'ciop', 'axis'].includes(key)) {
        const unit = key === 'cct' ? 'µm' : key === 'axis' ? '°' : 'mmHg';
        return [
            {
                id: generateElementId(key + value),
                value: value,
                unit: unit,
                custom: `${value} ${unit}`,
            },
        ];
    } else if (['cyl', 'sph'].includes(key)) {
        return [
            {
                id: generateElementId(key + value + '+'),
                value: value,
                unit: '+',
                custom: `${'+'}${value}`,
            },
            {
                id: generateElementId(key + value + '-'),
                value: value,
                unit: '-',
                custom: `${'-'}${value}`,
            },
        ];
    } else if ('add' === key) {
        return [
            {
                id: generateElementId(key + value + '+'),
                value: value,
                unit: '+',
                custom: `${'+'}${value}`,
            },
        ];
    }
    return [{ id: generateElementId(text), value: text, custom: text, unit: '' }];
};

export const generateOptions = (
    key: string,
): { id: string; value: string; unit: string; custom: string }[] => {
    const options: { id: string; value: string; unit: string; custom: string }[] = [];
    switch (key) {
        case 'nct':
        case 'gat':
        case 'ciop':
            for (let i = 0; i <= 80; i++) {
                options.push({
                    id: generateElementId(key + i.toString()),
                    value: i.toString(),
                    unit: 'mmHg',
                    custom: `${i} mmHg`,
                });
            }
            break;
        case 'cct':
            for (let i = 300; i <= 800; i++) {
                options.push({
                    id: generateElementId(key + i.toString()),
                    value: i.toString(),
                    unit: 'µm',
                    custom: `${i} µm`,
                });
            }
            break;
        case 'add':
            options.push({
                id: generateElementId(key + '0.00' + '0'),
                value: '+0.00',
                unit: '',
                custom: '+0.00',
            });
            for (let i = 0.25; i <= 3.75; i += 0.25) {
                options.push({
                    id: generateElementId(key + i.toFixed(2).toString()),
                    value: i.toFixed(2).toString(),
                    unit: '+',
                    custom: `+${i.toFixed(2)}`,
                });
            }
            break;
        case 'cyl':
            options.push({
                id: generateElementId(key + '0.00' + '0'),
                value: '+0.00',
                unit: '',
                custom: '+0.00',
            });
            for (let i = 0.25; i <= 8; i += 0.25) {
                options.push(
                    ...[
                        {
                            id: generateElementId(key + i.toString() + '+'),
                            value: i.toFixed(2).toString(),
                            unit: '+',
                            custom: `+${i.toFixed(2)}`,
                        },
                        {
                            id: generateElementId(key + i.toString() + '-'),
                            value: i.toFixed(2).toString(),
                            unit: '-',
                            custom: `-${i.toFixed(2)}`,
                        },
                    ],
                );
            }
            break;
        case 'axis':
            for (let i = 0; i <= 180; i += 5) {
                options.push({
                    id: generateElementId(key + i.toString() + '+'),
                    value: i.toString(),
                    unit: '°',
                    custom: `${i}°`,
                });
            }
            break;
        case 'sph':
            options.push({
                id: generateElementId(key + '0.00' + '0'),
                value: '+0.00',
                unit: '',
                custom: '+0.00',
            });
            for (let i = 0.25; i <= 13; i += 0.25) {
                options.push(
                    ...[
                        {
                            id: generateElementId(key + i.toString() + '+'),
                            value: i.toFixed(2).toString(),
                            unit: '+',
                            custom: `+${i.toFixed(2)}`,
                        },
                        {
                            id: generateElementId(key + i.toString() + '-'),
                            value: i.toFixed(2).toString(),
                            unit: '-',
                            custom: `-${i.toFixed(2)}`,
                        },
                    ],
                );
            }
            break;
        default:
            break;
    }
    return options;
};

export const getOpPropertyPlaceholder = <T>(key: keyof T): string => {
    switch (key) {
        case 'nct':
            return 'e.g: 15 mmHg';
        case 'gat':
            return 'e.g: 15 mmHg';
        case 'cct':
            return 'e.g: 500 µm';
        case 'ciop':
            return 'e.g: 15 mmHg';
        case 'sph':
            return 'e.g: -1.5';
        case 'cyl':
            return 'e.g: -1.5';
        case 'axis':
            return 'e.g: 90°';
        case 'add':
            return 'e.g: +1.5';
        case 'nva':
        case 'ucnva':
        case 'bcnva':
            return 'e.g: N6';
        case 'ucdva':
        case 'bcdva':
        case 'dva':
            return 'e.g: 6/6';

        case 'ph':
            return 'e.g: 6/6, HM, PL';
        default:
    }

    return 'e.g: 6/6';
};

export const getColumns = (
    pxKey: PX_OP_KEYS,
):
    | { key: keyof Omit<OpVision, 'eye'>; title: string }[]
    | { key: keyof Omit<OpIop, 'eye'>; title: string }[]
    | { key: keyof Omit<OpSpec, 'eye'>; title: string }[]
    | { key: keyof Omit<OpSpec, 'eye'>; title: string }[] => {
    switch (pxKey) {
        case 'opVision':
            return [
                {
                    key: 'ucdva',
                    title: 'UCDVA',
                },
                {
                    key: 'ucnva',
                    title: 'UCNVA',
                },
                {
                    key: 'ph',
                    title: 'Pinhole',
                },
                {
                    key: 'bcdva',
                    title: 'BCDVA',
                },
                {
                    key: 'bcnva',
                    title: 'BCNVA',
                },
            ];
        case 'opIop':
            return [
                {
                    key: 'nct',
                    title: 'NCT',
                },
                {
                    key: 'gat',
                    title: 'GAT',
                },
                {
                    key: 'cct',
                    title: 'CCT',
                },
                {
                    key: 'ciop',
                    title: 'CIOP',
                },
            ];
        case 'opCurrentSpec':
        case 'opAutoRefraction':
        case 'opSubjectiveRefraction':
        case 'opFinalPrescription':
            return [
                {
                    key: 'sph',
                    title: 'SPH',
                },
                {
                    key: 'cyl',
                    title: 'CYL',
                },
                {
                    key: 'axis',
                    title: 'AXIS',
                },
                {
                    key: 'add',
                    title: 'ADD',
                },
                {
                    key: 'dva',
                    title: 'DVA',
                },
                {
                    key: 'nva',
                    title: 'NVA',
                },
            ];
    }
};

export const rxKeyToHeadingMap: { [k in PX_OP_KEYS]: string } = {
    opVision: 'Visual Acuity Test',
    opIop: 'IOP',
    opCurrentSpec: 'Current glass prescription',
    opSubjectiveRefraction: 'Subjective Refraction',
    opAutoRefraction: 'Auto Refraction',
    opFinalPrescription: 'Final Glass Prescription',
};

export const rxKeyToIconMap: { [k in PX_OP_KEYS]: JSX.Element } = {
    opVision: visionIcon(),
    opIop: iopIcon(),
    opCurrentSpec: specIcon(),
    opSubjectiveRefraction: specIcon(),
    opAutoRefraction: specIcon(),
    opFinalPrescription: rxIcon(),
};

export const propertyMap: {
    [k in PX_OP_KEYS]: {
        [k: string]: { [k: string]: { propertyName: string; propertyId: string } };
    };
} = {
    opVision: {
        'sm-5179794949': {},
        'sm-5179735365': {},
        'ek-2676755484': {
            ucdva: { propertyName: 'UCDVA', propertyId: 'lb-3845301997' },
            ucnva: { propertyName: 'UCNVA', propertyId: 'lb-4300148510' },
            ph: { propertyName: 'Pinhole', propertyId: 'sm-6180137981' },
            bcdva: { propertyName: 'BCDVA', propertyId: 'lb-9161562896' },
            bcnva: { propertyName: 'BCNVA', propertyId: 'lb-9494033034' },
        },
        'ek-4343861153': {
            ucdva: { propertyName: 'UCDVA', propertyId: 'lb-9865456929' },
            ucnva: { propertyName: 'UCNVA', propertyId: 'lb-6976903591' },
            ph: { propertyName: 'Pinhole', propertyId: 'sm-7893075215' },
            bcdva: { propertyName: 'BCDVA', propertyId: 'lb-4875658894' },
            bcnva: { propertyName: 'BCNVA', propertyId: 'lb-1983157931' },
        },
    },
    opIop: {
        'sm-5179794949': {},
        'sm-5179735365': {},
        'ek-2676755484': {
            nct: { propertyName: 'NCT', propertyId: 'p-8477329440' },
            gat: { propertyName: 'GAT', propertyId: 'p-8477389030' },
            cct: { propertyName: 'CCT', propertyId: 'lb-7946860831' },
            ciop: { propertyName: 'CIOP', propertyId: 'ek-30697830' },
        },
        'ek-4343861153': {
            nct: { propertyName: 'NCT', propertyId: 'p-8477329440' },
            gat: { propertyName: 'GAT', propertyId: 'p-8477389030' },
            cct: { propertyName: 'CCT', propertyId: 'lb-9088803028' },
            ciop: { propertyName: 'CIOP', propertyId: 'ek-30697830' },
        },
    },
    opCurrentSpec: {
        'sm-5179794949': {},
        'sm-5179735365': {},
        'ek-2676755484': {
            sph: { propertyName: 'SPH', propertyId: 'ek-1005836' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-9958676529' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-6190026618' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-9494033034' },
        },
        'ek-4343861153': {
            sph: { propertyName: 'SPH', propertyId: 'ek-1005836' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-9958676529' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-6190026618' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-1983157931' },
        },
    },
    opFinalPrescription: {
        'sm-5179794949': {},
        'sm-5179735365': {},
        'ek-2676755484': {
            sph: { propertyName: 'SPH', propertyId: 'ek-1005836' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-9958676529' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-6190026618' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-9494033034' },
        },
        'ek-4343861153': {
            sph: { propertyName: 'SPH', propertyId: 'ek-1005836' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-9958676529' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-6190026618' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-1983157931' },
        },
    },
    opSubjectiveRefraction: {
        'sm-5179794949': {
            sph: { propertyName: 'SPH', propertyId: 'lb-169241686' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-5495098091' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-6012578000' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-4300148510' },
        },
        'sm-5179735365': {
            sph: { propertyName: 'SPH', propertyId: 'lb-8583432810' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-7464912049' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-7595769384' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-6976903591' },
        },
        'ek-2676755484': {
            sph: { propertyName: 'SPH', propertyId: 'lb-5429300993' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-530849565' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-5885630465' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-9494033034' },
        },
        'ek-4343861153': {
            sph: { propertyName: 'SPH', propertyId: 'lb-6269225863' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-1595082269' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-8374877290' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-1983157931' },
        },
    },
    opAutoRefraction: {
        'sm-5179794949': {
            sph: { propertyName: 'SPH', propertyId: 'lb-169241686' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-5495098091' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-6012578000' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-4300148510' },
        },
        'sm-5179735365': {
            sph: { propertyName: 'SPH', propertyId: 'lb-8583432810' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-7464912049' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-7595769384' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-6976903591' },
        },
        'ek-2676755484': {
            sph: { propertyName: 'SPH', propertyId: 'lb-5429300993' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-530849565' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-5885630465' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-9494033034' },
        },
        'ek-4343861153': {
            sph: { propertyName: 'SPH', propertyId: 'lb-6269225863' },
            cyl: { propertyName: 'CYL', propertyId: 'lb-1595082269' },
            axis: { propertyName: 'AXIS', propertyId: 'lb-8374877290' },
            add: { propertyName: 'ADD', propertyId: 'ek-988162' },
            dva: { propertyName: 'DVA', propertyId: 'ek-991600' },
            nva: { propertyName: 'NVA', propertyId: 'lb-1983157931' },
        },
    },
};

export function getDefaulOPValue(
    pxKey: PX_OP_KEYS,
): (OptionTypeBase & { id: string; name?: string })[] {
    let defaultValue: (OptionTypeBase & { id: string; name?: string })[] = [
        {
            id: `ek-2676755484`,
            name: eyeNameMap['RE'],
            eye: {
                value: 'RE',
            },
        },
        {
            id: `ek-4343861153`,
            name: eyeNameMap['LE'],
            eye: {
                value: 'LE',
            },
        },
    ];

    if (pxKey === 'opSubjectiveRefraction' || pxKey === 'opAutoRefraction') {
        defaultValue = [
            {
                id: `ek-2676755484`,
                name: eyeNameMap['RE'],
                eye: {
                    value: 'RE (Undilated)',
                },
            },
            {
                id: `ek-4343861153`,
                name: eyeNameMap['LE'],
                eye: {
                    value: 'LE (Undilated)',
                },
            },
            {
                id: `sm-5179794949`,
                name: eyeNameMap['RE'],
                eye: {
                    value: 'RE (Dilated)',
                },
            },
            {
                id: `sm-5179735365`,
                name: eyeNameMap['LE'],
                eye: {
                    value: 'LE (Dilated)',
                },
            },
        ];
    }
    return defaultValue;
}

export const getCopiedvalue = (
    from: OP_FGP_COPY_FROM,
    id: string,
    rx: Prescription,
    key: keyof Omit<OpVision, 'eye'> | keyof Omit<OpSpec, 'eye'> | keyof Omit<OpIop, 'eye'>,
):
    | {
          id: string;
          value?: string;
          custom?: string;
      }
    | undefined => {
    if (from === 'cgp') {
        // Sorry for this hack, but it is needed to get the value from the correct object
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return rx.opCurrentSpec?.find((r) => r.id === id)?.[key];
    } else if (from === 'usr') {
        // Sorry for this hack, but it is needed to get the value from the correct object
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return rx.opSubjectiveRefraction?.find((r) => r.id === id)?.[key];
    } else if (from === 'uar') {
        // Sorry for this hack, but it is needed to get the value from the correct object
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return rx.opAutoRefraction?.find((r) => r.id === id)?.[key];
    } else if (from === 'dar') {
        if (id === `ek-2676755484`) {
            // Sorry for this hack, but it is needed to get the value from the correct object
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return rx.opAutoRefraction?.find((r) => r.id === 'sm-5179794949')?.[key];
        } else if (id === `ek-4343861153`) {
            // Sorry for this hack, but it is needed to get the value from the correct object
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return rx.opAutoRefraction?.find((r) => r.id === 'sm-5179735365')?.[key];
        }
    } else if (from === 'dsr') {
        if (id === `ek-2676755484`) {
            // Sorry for this hack, but it is needed to get the value from the correct object
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return rx.opSubjectiveRefraction?.find((r) => r.id === 'sm-5179794949')?.[key];
        } else if (id === `ek-4343861153`) {
            // Sorry for this hack, but it is needed to get the value from the correct object
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return rx.opSubjectiveRefraction?.find((r) => r.id === 'sm-5179735365')?.[key];
        }
    }
    return undefined;
};
